import type { WorkflowSelectedValues } from "./choiceModel";
import { useEffect, useRef, useState } from "react";
import { CheckCircle, Edit2, RotateCcw, Save, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "../../components/ui";
import { DocumentationCardEditor } from "./DocumentationCardEditor";
import { DocumentationNoteDateTime, stockholmDateTime } from "./DocumentationNoteView";
import { processingFailure } from "./processingFailure";
import { WorkflowInstanceSwitcher } from "./WorkflowInstanceSwitcher";
import { WorkflowNodeResults } from "./WorkflowNodeResults";
import { WorkflowResultCard } from "./WorkflowResultPresentation";
import type { DocumentationDraft } from "./models";
import type { DocumentationCardChanges, DocumentationRevisionChanges } from "./api";
import { buildWorkflowNodeChanges } from "./workflowNodeResultUtils";
import styles from "./DocumentationWorkspace.module.css";
import flow from "../../components/DocumentFlow/DocumentFlow.module.css";

type Edits = { choices: WorkflowSelectedValues; nodes: Record<string, string>; fields: Record<string, unknown>; nodesConfirmed: Set<string>; fieldsConfirmed: Set<string> };
const emptyEdits = (): Edits => ({ choices: {}, nodes: {}, fields: {}, nodesConfirmed: new Set(), fieldsConfirmed: new Set() });
type Props = {
    draft: DocumentationDraft;
    busy: boolean;
    onSave: (instanceId: string, changes: DocumentationCardChanges) => Promise<void>;
    onSubmit: (instanceId: string) => Promise<void>;
    onSubmitRevision?: (changes: DocumentationRevisionChanges) => Promise<void>;
    onReplaceWorkflow?: (instanceId: string, workflowId: string) => Promise<void>;
    onRetryGeneration?: (instanceId: string) => Promise<void>;
    onDirtyChange: (draftId: string, dirty: boolean) => void;
    onSourceNoteClick: (noteId: string, projectId?: string) => void;
};

export const WorkflowCardsReview = ({ draft, busy, onSave, onSubmit, onSubmitRevision, onRetryGeneration, onReplaceWorkflow, onDirtyChange, onSourceNoteClick }: Props) => {
    const { t } = useTranslation();
    // Keep per-card edits here so switching tabs cannot discard an unfinished review.
    const [edits, setEdits] = useState<Record<string, Edits>>({});
    const [editing, setEditing] = useState<Record<string, boolean>>({});
    const [workflowSelections, setWorkflowSelections] = useState<Record<string, string>>({});
    const [replacing, setReplacing] = useState<{ instanceId: string; workflowId: string }>();
    const replaceTrigger = useRef<HTMLButtonElement | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [pending, setPending] = useState<string>();
    const [confirming, setConfirming] = useState<string>();
    const signTrigger = useRef<HTMLButtonElement | null>(null);
    const revision = Boolean(draft.note_edit?.requires_review);
    const initialCard = !draft.note_edit;
    useEffect(() => setConfirming(undefined), [edits]);
    const dirty = (draft.workflow_instances ?? []).some(instance => Boolean(workflowSelections[instance.id] && workflowSelections[instance.id] !== instance.workflow_id)) || Object.values(edits).some(
        value => Object.keys(value.nodes).length || Object.keys(value.choices).length || Object.keys(value.fields).length || value.nodesConfirmed.size || value.fieldsConfirmed.size
    );
    useEffect(() => {
        onDirtyChange(draft.id, dirty);
    }, [draft.id, dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(draft.id, false), [draft.id, onDirtyChange]);
    const locked = busy || Boolean(pending) || draft.status === "approved" || draft.status === "finalizing";

    const confirmSubmission = async () => {
        if (!confirming || locked) return;
        const instanceId = confirming;
        setPending(instanceId);
        setErrors(current => ({ ...current, submission: "" }));
        try {
            if (revision && onSubmitRevision) {
                const changes: DocumentationRevisionChanges = {};
                for (const instance of draft.workflow_instances ?? []) {
                    const value = edits[instance.id];
                    if (value)
                        changes[instance.id] = {
                            changes: buildWorkflowNodeChanges(instance.workflow_node_results, value.nodes),
                            selected_values: value.choices,
                            confirmations: [...value.nodesConfirmed],
                            field_changes: value.fields,
                            field_confirmations: [...value.fieldsConfirmed]
                        };
                }
                await onSubmitRevision(changes);
                setEdits({});
                setEditing({});
            } else {
                await onSubmit(instanceId);
            }
            setConfirming(undefined);
        } catch (error: unknown) {
            setErrors(current => ({ ...current, submission: error instanceof Error ? error.message : String(error) }));
        } finally {
            setPending(undefined);
        }
    };

    return (
        <>
            <WorkflowInstanceSwitcher
                header={<DocumentationNoteDateTime {...stockholmDateTime(draft.created_at)} />}
                tabs={(draft.workflow_instances || []).map(instance => {
                    const submitted = (draft.submitted_card_ids ?? []).includes(instance.id);
                    const carriedForward = revision && Boolean(draft.review_card_ids) && !draft.review_card_ids?.includes(instance.id);
                    const value = edits[instance.id] || emptyEdits();
                    const saved = (draft.saved_card_ids || []).includes(instance.id);
                    const selectedWorkflowId = workflowSelections[instance.id] || instance.workflow_id;
                    const workflowChanged = selectedWorkflowId !== instance.workflow_id;
                    const changed = workflowChanged ||
                        (draft.changed_saved_card_ids || []).includes(instance.id) ||
                        Boolean(Object.keys(value.nodes).length || Object.keys(value.choices).length || Object.keys(value.fields).length || value.nodesConfirmed.size || value.fieldsConfirmed.size);
                    const isEditing = !submitted && editing[instance.id] && draft.status !== "approved";
                    const cardDraft: DocumentationDraft = {
                        ...draft,
                        id: `${draft.id}-${instance.id}`,
                        fields: instance.fields,
                        workflow_node_results: instance.workflow_node_results,
                        validation: instance.validation,
                        context_options: instance.context_options,
                        definition: instance.definition,
                        selected_workflow: { workflow_id: instance.workflow_id, title: instance.workflow_title, selected_by: instance.selected_by }
                    };
                    const update = (patch: Partial<Edits>) =>
                        setEdits(current => ({ ...current, [instance.id]: { ...(current[instance.id] || emptyEdits()), ...patch } }));
                    const confirm = (kind: "nodesConfirmed" | "fieldsConfirmed", key: string, checked: boolean) => {
                        const next = new Set(value[kind]);
                        if (checked) next.add(key);
                        else next.delete(key);
                        update({ [kind]: next });
                    };
                    const submit = async (action: "save" | "remove" | "retry") => {
                        setPending(instance.id);
                        setErrors(current => ({ ...current, [instance.id]: "" }));
                        try {
                            await onSave(instance.id, {
                                action,
                                selected_values: action === "save" ? value.choices : {},
                                changes: action === "save" ? buildWorkflowNodeChanges(instance.workflow_node_results, value.nodes) : {},
                                confirmations: action === "save" ? [...value.nodesConfirmed] : [],
                                field_changes: action === "save" ? value.fields : {},
                                field_confirmations: action === "save" ? [...value.fieldsConfirmed] : []
                            });
                            setEdits(current => ({ ...current, [instance.id]: emptyEdits() }));
                            setEditing(current => ({ ...current, [instance.id]: false }));
                        } catch (error) {
                            setErrors(current => ({ ...current, [instance.id]: error instanceof Error ? error.message : String(error) }));
                        } finally {
                            setPending(undefined);
                        }
                    };
                    return {
                        id: instance.id,
                        title: instance.workflow_title,
                        workflowSelection: isEditing && onReplaceWorkflow && initialCard
                            ? {
                                label: t("documentFlow.workflow"),
                                value: selectedWorkflowId,
                                options: draft.workflow_options ?? [],
                                disabled: locked || Boolean(draft.pending_card_id),
                                onChange: (workflowId: string) => setWorkflowSelections(current => ({ ...current, [instance.id]: workflowId }))
                            }
                            : undefined,
                        content: (
                            <WorkflowResultCard
                                title={instance.workflow_title}
                                status={submitted
                                    ? t("documentFlow.cardSubmitted", { defaultValue: "Submitted" })
                                    : saved
                                      ? t(changed ? "documentFlow.cardChanged" : "documentFlow.saved", {
                                            defaultValue: changed ? "Unsaved changes" : "Saved"
                                        })
                                      : t("documentFlow.cardDraft", { defaultValue: "Draft" })}
                            >
                                {isEditing && workflowChanged && (
                                    <p className={styles.workflowChangeHint}>{t("documentFlow.replaceCardWorkflowConfirm")}</p>
                                )}
                                {instance.status === "failed" ? (
                                    <>
                                        <p role="alert" className={styles.errorBox}>
                                            {processingFailure(instance.error?.code) ?? t("documentFlow.processingFailed")}
                                        </p>
                                        {onRetryGeneration && draft.status !== "approved" && (
                                            <div className={flow.messageActions}>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    leftIcon={<RotateCcw size={16} />}
                                                    disabled={locked || Boolean(draft.pending_card_id)}
                                                    isLoading={pending === instance.id}
                                                    onClick={async () => {
                                                        setPending(instance.id);
                                                        setErrors(current => ({ ...current, [instance.id]: "" }));
                                                        try {
                                                            await onRetryGeneration(instance.id);
                                                        } catch (error) {
                                                            setErrors(current => ({
                                                                ...current,
                                                                [instance.id]: error instanceof Error ? error.message : String(error)
                                                            }));
                                                        } finally {
                                                            setPending(undefined);
                                                        }
                                                    }}
                                                >
                                                    {t("documentFlow.retry")}
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {isEditing ? (
                                            <DocumentationCardEditor
                                                draft={cardDraft}
                                                workflowChangeHint={t("documentFlow.replaceCardWorkflowConfirm")}
                                                selectedWorkflowId={selectedWorkflowId}
                                                allowWorkflowSelection={false}
                                                disabled={locked}
                                                fieldEdits={value.fields}
                                                fieldConfirmations={value.fieldsConfirmed}
                                                nodeEdits={value.nodes}
                                                selectedValues={value.choices}
                                                onChoiceEdit={(key, selectedValue, content) => update({ choices: { ...value.choices, [key]: selectedValue }, nodes: { ...value.nodes, [key]: content } })}
                                                nodeConfirmations={value.nodesConfirmed}
                                                onWorkflowSelection={workflowId => setWorkflowSelections(current => ({ ...current, [instance.id]: workflowId }))}
                                                onSourceNoteClick={onSourceNoteClick}
                                                onFieldEdit={(key, content) => update({ fields: { ...value.fields, [key]: content } })}
                                                onNodeEdit={(key, content) => update({ nodes: { ...value.nodes, [key]: content } })}
                                                onFieldConfirmation={(key, checked) => confirm("fieldsConfirmed", key, checked)}
                                                onNodeConfirmation={(key, checked) => confirm("nodesConfirmed", key, checked)}
                                            />
                                        ) : (
                                            <>
                                                <WorkflowNodeResults
                                                    nodes={instance.workflow_node_results}
                                                    goals={instance.context_options?.active_goals}
                                                    sourceNotes={instance.context_options?.signed_notes}
                                                    edits={value.nodes}
                                                    selectedValues={value.choices}
                                                    onChoiceEdit={(key, selectedValue, content) => update({ choices: { ...value.choices, [key]: selectedValue }, nodes: { ...value.nodes, [key]: content } })}
                                                    confirmations={new Set()}
                                                    disabled={locked || submitted}
                                                    readOnly
                                                    allowIbicSelection={!submitted}
                                                    allowChoiceSelection={!submitted}
                                                    onEdit={(key, content) => update({ nodes: { ...value.nodes, [key]: content } })}
                                                    onConfirmation={() => undefined}
                                                    onSourceNoteClick={onSourceNoteClick}
                                                />
                                            </>
                                        )}
                                        {submitted && (
                                            <p>
                                                {carriedForward
                                                    ? t("documentFlow.cardUnchanged", {
                                                          defaultValue: "Unchanged. Existing review and signature records are retained."
                                                      })
                                                    : t("documentFlow.cardSubmittedHelp", {
                                                          defaultValue: "Submitted. Assigned boxes are handled in Workspace."
                                                      })}
                                            </p>
                                        )}
                                        {!submitted && !revision && (
                                            <p>
                                                {t(draft.note_edit ? "documentFlow.cardDraftHelp" : "documentFlow.cardWorkingDraftHelp", {
                                                    defaultValue: draft.note_edit
                                                        ? "Save keeps this card editable and does not dispatch tasks. Save your changes, then Submit when ready."
                                                        : "Save keeps this card in Workspace · Working so you can continue later. Choose Sign when ready to send this card's boxes for review. One input creates one Note per project. Each workflow joins that Note once its required signatures are complete, without waiting for other workflows."
                                                })}
                                            </p>
                                        )}
                                        {draft.status !== "approved" && !submitted && (
                                            <div className={flow.messageActions}>
                                                {draft.pending_card_id ? (
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        leftIcon={<RotateCcw size={16} />}
                                                        disabled={locked || draft.pending_card_id !== instance.id}
                                                        onClick={() => void submit("retry")}
                                                    >
                                                        {t("documentFlow.retry")}
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            leftIcon={isEditing ? <X size={16} /> : <Edit2 size={16} />}
                                                            disabled={locked}
                                                            onClick={() => {
                                                                if (isEditing) {
                                                                    setEdits(current => ({ ...current, [instance.id]: emptyEdits() }));
                                                                    setWorkflowSelections(current => ({ ...current, [instance.id]: instance.workflow_id }));
                                                                }
                                                                setEditing(current => ({ ...current, [instance.id]: !isEditing }));
                                                            }}
                                                        >
                                                            {t(isEditing ? "tooltips.cancel" : "documentFlow.editNote")}
                                                        </Button>
                                                        {!revision && (
                                                            <Button
                                                                variant={saved && !isEditing && !changed ? "success" : "secondary"}
                                                                size="sm"
                                                                leftIcon={saved && !isEditing && !changed ? <CheckCircle size={16} /> : <Save size={16} />}
                                                                disabled={
                                                                    locked ||
                                                                    (saved && !isEditing && !changed && draft.status !== "finalization_failed") ||
                                                                    (!workflowChanged && Object.values(value.nodes).some(content => !content.trim()))
                                                                }
                                                                onClick={event => {
                                                                    if (workflowChanged) {
                                                                        replaceTrigger.current = event.currentTarget;
                                                                        setReplacing({ instanceId: instance.id, workflowId: selectedWorkflowId });
                                                                        setErrors(current => ({ ...current, replacement: "" }));
                                                                    } else void submit("save");
                                                                }}
                                                            >
                                                                {t(workflowChanged ? "documentFlow.replaceCardWorkflow" : saved && !isEditing && !changed ? "documentFlow.saved" : "tooltips.save")}
                                                            </Button>
                                                        )}
                                                        {!revision && (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                disabled={locked || !saved || changed}
                                                                onClick={event => {
                                                                    signTrigger.current = event.currentTarget;
                                                                    setErrors(current => ({ ...current, submission: "" }));
                                                                    setConfirming(instance.id);
                                                                }}
                                                            >
                                                                {initialCard
                                                                    ? t("documentFlow.signCard", { defaultValue: "Sign" })
                                                                    : t("documentFlow.submitCard", { defaultValue: "Submit" })}
                                                            </Button>
                                                        )}
                                                        {saved && draft.note_edit && !revision && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                leftIcon={<RotateCcw size={16} />}
                                                                disabled={locked}
                                                                onClick={() => void submit("remove")}
                                                            >
                                                                {t("documentFlow.removeCard", { defaultValue: "Remove from Note" })}
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                                {errors[instance.id] && (
                                    <p role="alert" className={styles.errorBox}>
                                        {errors[instance.id]}
                                    </p>
                                )}
                            </WorkflowResultCard>
                        )
                    };
                })}
            />
            {revision && !draft.review_card_ids && (
                <div className={flow.messageActions}>
                    <p>
                        {t("documentFlow.revisionHelp", {
                            defaultValue:
                                "Only changed boxes with assigned reviewers require review. Changes without review are published immediately; mixed changes are published together after review."
                        })}
                    </p>
                    <Button disabled={locked || !dirty} onClick={event => {
                        signTrigger.current = event.currentTarget;
                        setConfirming("revision");
                    }}>
                        {t("documentFlow.submitRevision", { defaultValue: "Submit revision" })}
                    </Button>
                </div>
            )}
            {revision && draft.review_card_ids && (
                <p role="status">
                    {draft.finalization?.stage === "revision_published"
                        ? t("documentFlow.revisionPublished", { defaultValue: "The reviewed revision has been published as a new Note version." })
                        : t("documentFlow.revisionSubmitted", {
                              defaultValue:
                                  "Revision submitted. The published Note remains unchanged until every changed box requiring review is completed. Continue in Workspace."
                          })}
                </p>
            )}
            <Modal
                open={Boolean(replacing)}
                size="sm"
                onOpenChange={open => { if (!open && !locked) setReplacing(undefined); }}
                onCloseAutoFocus={event => { event.preventDefault(); replaceTrigger.current?.focus(); }}
            >
                <Modal.Header title={t("documentFlow.replaceCardWorkflow")} description={t("documentFlow.replaceCardWorkflowConfirm")} />
                {errors.replacement && <Modal.Body><p role="alert" className={styles.errorBox}>{errors.replacement}</p></Modal.Body>}
                <Modal.Footer>
                    <Button variant="secondary" disabled={locked} onClick={() => setReplacing(undefined)}>{t("tooltips.cancel")}</Button>
                    <Button disabled={locked} isLoading={Boolean(pending)} onClick={async () => {
                        if (!replacing || !onReplaceWorkflow || locked) return;
                        const { instanceId, workflowId } = replacing;
                        setPending(instanceId);
                        setErrors(current => ({ ...current, replacement: "" }));
                        try {
                            await onReplaceWorkflow(instanceId, workflowId);
                            setEdits(current => ({ ...current, [instanceId]: emptyEdits() }));
                            setEditing(current => ({ ...current, [instanceId]: false }));
                            setWorkflowSelections(current => {
                                const next = { ...current };
                                delete next[instanceId];
                                return next;
                            });
                            setReplacing(undefined);
                        } catch (error: unknown) {
                            setErrors(current => ({ ...current, replacement: error instanceof Error ? error.message : String(error) }));
                        } finally { setPending(undefined); }
                    }}>{t("documentFlow.replaceCardWorkflow")}</Button>
                </Modal.Footer>
            </Modal>
            <Modal
                open={(initialCard || revision) && Boolean(confirming)}
                size="sm"
                onOpenChange={open => {
                    if (!open && !locked) setConfirming(undefined);
                }}
                onCloseAutoFocus={event => {
                    event.preventDefault();
                    signTrigger.current?.focus();
                }}
            >
                <Modal.Header
                    className={styles.signConfirmationHeader}
                    title={revision
                        ? t("documentFlow.submitRevision", { defaultValue: "Submit revision" })
                        : t("documentFlow.signCardConfirmTitle", { defaultValue: "Sign card" })}
                    description={revision
                        ? t("documentFlow.revisionConfirmShort", { defaultValue: "Submit your changes? A review will be requested if needed." })
                        : t("documentFlow.signCardConfirm", {
                            defaultValue: "Sign this card? It will become read-only and its boxes will be sent to Workspace."
                        })}

                />
                {errors.submission && (
                    <Modal.Body>
                        <p role="alert" className={styles.errorBox}>{errors.submission}</p>
                    </Modal.Body>
                )}
                <Modal.Footer className={styles.signConfirmationFooter}>
                    <Button variant="secondary" disabled={locked} onClick={() => setConfirming(undefined)}>
                        {t("tooltips.cancel")}
                    </Button>
                    <Button variant="primary" disabled={locked} isLoading={Boolean(pending)} onClick={() => void confirmSubmission()}>
                        {revision
                            ? t("documentFlow.confirmSubmission", { defaultValue: "Confirm submission" })
                            : t("documentFlow.signCard", { defaultValue: "Sign" })}
                    </Button>
                </Modal.Footer>
            </Modal>
            {confirming && !initialCard && !revision && (
                <section
                    className={styles.infoBox}
                    aria-label={
                        initialCard
                            ? t("documentFlow.signCardConfirmTitle", { defaultValue: "Sign card" })
                            : t("documentFlow.confirmSubmission", { defaultValue: "Confirm submission" })
                    }
                >
                    {!revision && <strong>{draft.workflow_instances?.find(instance => instance.id === confirming)?.workflow_title}</strong>}
                    <p>
                        {revision
                            ? t("documentFlow.revisionConfirm", {
                                  defaultValue:
                                      "Submit these changes? Only changed boxes with assigned reviewers will be sent for review. Unchanged boxes retain their approvals. If no review is needed, a new Note version is published immediately."
                              })
                            : initialCard
                              ? t("documentFlow.signCardConfirm", {
                                    defaultValue: "Sign this saved card? Its boxes will be sent to Workspace and this card will become read-only here."
                                })
                              : t("documentFlow.submitCardConfirm", {
                                    defaultValue: "Submit this saved card? Its boxes will be dispatched and this card will become read-only here."
                                })}
                    </p>
                    <div className={flow.messageActions}>
                        <Button variant="secondary" disabled={locked} onClick={() => setConfirming(undefined)}>
                            {t("tooltips.cancel")}
                        </Button>
                        <Button disabled={locked} onClick={() => void confirmSubmission()}>
                            {initialCard
                                ? t("documentFlow.signCard", { defaultValue: "Sign" })
                                : t("documentFlow.confirmSubmission", { defaultValue: "Confirm submission" })}
                        </Button>
                    </div>
                </section>
            )}
            {errors.submission && !((initialCard || revision) && confirming) && (
                <p role="alert" className={styles.errorBox}>
                    {errors.submission}
                </p>
            )}
        </>
    );
};
